/**
 * The host's create-room form and its mapping onto `CREATE_ROOM`.
 *
 * Pure on purpose: the create screen only ever edits an `OnlineRoomForm`, so
 * the form → wire mapping is unit-testable without a DOM or a socket.
 *
 * **The picker offers 2–4 humans, not 1–4** (the plan's open question #4). A
 * one-human room is solo play over a socket, which free play already does
 * offline and better. A lone host can still end up alone — pressing "Start
 * now" before anyone joins shrinks the room to its occupancy server-side and
 * bot-fills the rest — that path simply isn't something the picker offers.
 *
 * **No seat preference.** `preferredSeat` only rotates wind *labels* here:
 * every client already sees itself at the bottom (`layout/types.ts`'s
 * `seatPosition`), so offering the choice would promise something it does not
 * deliver. The field is deliberately absent from `CreateRoomArgs`.
 */

import { SEAT_COUNT } from '../game/types.js';
import type { RulesetId } from './protocol.js';
import { ROOM_CODE_ALPHABET, ROOM_CODE_LENGTH, sanitizeRoomCodeInput } from '@bn-games/network-protocol';

/** The table is always four seats; the *room* sizes to the humans (plan D1). */
export const MAHJONG_TABLE_SEATS = SEAT_COUNT;

/** `RoomServer` clamps a create to `[2, maxSeats]`, so 2 is the floor here too. */
export const MIN_HUMAN_SEATS = 2;

/** Every human count the create screen offers, low to high. */
export const HUMAN_SEAT_OPTIONS: readonly number[] = Array.from(
  { length: MAHJONG_TABLE_SEATS - MIN_HUMAN_SEATS + 1 },
  (_, i) => MIN_HUMAN_SEATS + i,
);

/** Mahjong has no player profile yet, so every seat introduces itself the same. */
export const DEFAULT_PLAYER_NAME = 'Player';

export interface OnlineRoomForm {
  readonly rulesetId: RulesetId;
  /** How many seats are held for humans; the rest are bots from the first deal. */
  readonly humanSeats: number;
  /** Deal the wall from a drand beacon round so every player can check it after. */
  readonly verifiable: boolean;
}

/**
 * A full table of humans by default — the friend-invite case. Verifiable is off
 * to match backgammon's and domino's defaults: a drand outage means no deal at
 * all, which is not a trade a casual table should make without asking.
 */
export function defaultRoomForm(rulesetId: RulesetId): OnlineRoomForm {
  return { rulesetId, humanSeats: MAHJONG_TABLE_SEATS, verifiable: false };
}

/** Exactly the `CREATE_ROOM` fields mahjong sends — see the header for what it omits. */
export interface CreateRoomArgs {
  readonly playerName: string;
  readonly variant: RulesetId;
  readonly seatCount: number;
  readonly verifiable: boolean;
}

export function clampHumanSeats(seats: number): number {
  if (!Number.isFinite(seats)) return MAHJONG_TABLE_SEATS;
  return Math.min(MAHJONG_TABLE_SEATS, Math.max(MIN_HUMAN_SEATS, Math.round(seats)));
}

/**
 * Room codes are `ROOM_CODE_LENGTH` characters from a deliberately
 * unambiguous alphabet — `@bn-games/network-protocol`'s `ROOM_CODE_ALPHABET`
 * omits I/O/0/1 so a code read aloud or copied off a screen can't be
 * mistyped into a different room. The join field mirrors that: input is
 * upper-cased and anything outside the alphabet is dropped as it is typed,
 * so lower case and stray punctuation are forgiven. Filtering is character-wise,
 * not a parser — pasting a code embedded in a sentence keeps that sentence's
 * letters too, so it will not find the code for you.
 *
 * `normalizeRoomCode` here is `sanitizeRoomCodeInput` under this module's
 * established name — the shared package's own `normalizeRoomCode` only
 * upper-cases (matching server leniency); this alias keeps the distinct
 * "sanitize free-form join-field input" behavior this screen has always had.
 */
export { ROOM_CODE_ALPHABET, ROOM_CODE_LENGTH };
export { sanitizeRoomCodeInput as normalizeRoomCode };

/**
 * A code is submittable only at full length once sanitized — a short or
 * malformed one always 404s. Deliberately not the shared package's own
 * `isCompleteRoomCode`: that one checks raw alphabet membership without
 * normalizing first, whereas this screen's `code` state (and callers of this
 * function) may still carry lower case or punctuation, so sanitizing first
 * is load-bearing here.
 */
export function isCompleteRoomCode(code: string): boolean {
  return sanitizeRoomCodeInput(code).length === ROOM_CODE_LENGTH;
}

export function createRoomArgs(
  form: OnlineRoomForm,
  playerName: string = DEFAULT_PLAYER_NAME,
): CreateRoomArgs {
  const trimmed = playerName.trim();
  return {
    playerName: trimmed === '' ? DEFAULT_PLAYER_NAME : trimmed,
    variant: form.rulesetId,
    seatCount: clampHumanSeats(form.humanSeats),
    verifiable: form.verifiable,
  };
}
